import { type BrowserTask, compareVersions, publisherRelease } from "@ondit/publishing";
import { useCallback, useEffect, useRef, useState } from "react";

type Status = "checking" | "connected" | "missing" | "outdated";

export function usePublisherExtension() {
	const [status, setStatus] = useState<Status>("checking");
	const [version, setVersion] = useState("");
	const [device, setDevice] = useState("");
	const probe = useRef({ id: "", resolve: (_: Status) => {} });
	const timeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
	const answer = useCallback((next: Status) => {
		clearTimeout(timeout.current);
		setStatus(next);
		probe.current.resolve(next);
		probe.current.resolve = () => {};
	}, []);
	/* A quiet check keeps what the page already shows until the answer arrives.
	 * Every return to this tab re-probes, and it used to drop "connected" to
	 * "checking" for that round trip: a click on "open and fill" in the gap
	 * found the assistant not connected and offered to install it. */
	const probeNow = useCallback(
		(quiet: boolean) =>
			new Promise<Status>((resolve) => {
				const id = crypto.randomUUID();
				const earlier = probe.current.resolve;
				probe.current = {
					id,
					resolve: (next) => {
						earlier(next);
						resolve(next);
					},
				};
				if (!quiet) setStatus("checking");
				clearTimeout(timeout.current);
				window.postMessage({ source: "ondit-panel", type: "ping", request_id: id }, location.origin);
				timeout.current = setTimeout(() => answer("missing"), 2000);
			}),
		[answer],
	);
	const check = useCallback(() => probeNow(false), [probeNow]);
	useEffect(() => {
		const receive = (event: MessageEvent) => {
			if (
				event.source !== window ||
				event.origin !== location.origin ||
				event.data?.source !== "ondit-extension" ||
				event.data.type !== "pong" ||
				event.data.request_id !== probe.current.id
			)
				return;
			setVersion(event.data.version ?? "0.2.0");
			setDevice(event.data.device_id ?? "");
			answer(
				event.data.protocol !== publisherRelease.protocol ||
					compareVersions(event.data.version ?? "0.2.0", publisherRelease.version) < 0
					? "outdated"
					: "connected",
			);
		};
		const refocus = () => void probeNow(true);
		window.addEventListener("message", receive);
		window.addEventListener("focus", refocus);
		refocus();
		return () => {
			clearTimeout(timeout.current);
			window.removeEventListener("message", receive);
			window.removeEventListener("focus", refocus);
		};
	}, [answer, probeNow]);
	return { status, check, version, device };
}

/** An acknowledgement means the assistant opened this saved snapshot, never that a post is public. */
export function openPublisher(task: BrowserTask, locale: string, scheduledAt?: string): Promise<void> {
	return new Promise((resolve, reject) => {
		const request = crypto.randomUUID();
		const done = (error?: Error) => {
			clearTimeout(timeout);
			window.removeEventListener("message", receive);
			error ? reject(error) : resolve();
		};
		const receive = (event: MessageEvent) => {
			const m = event.data;
			if (
				event.source !== window ||
				event.origin !== location.origin ||
				m?.source !== "ondit-extension" ||
				m.request_id !== request ||
				m.task_id !== task.id
			)
				return;
			if (m.type === "accepted") done();
			/* The assistant says why it refused; before it did, a signed-out session,
			 * a task edited elsewhere and a dead connection all read as the last one. */
			if (m.type === "rejected")
				done(new Error(["login_required", "task_changed"].includes(m.error) ? m.error : "publisher_unavailable"));
		};
		const timeout = setTimeout(() => done(new Error("publisher_unavailable")), 8000);
		window.addEventListener("message", receive);
		window.postMessage(
			{ source: "ondit-panel", type: "handoff", request_id: request, locale, task, scheduled_at: scheduledAt },
			location.origin,
		);
	});
}
